import {
	type MouseEvent,
	useCallback,
	useEffect,
	useRef,
	useState,
} from "react";
import { getConnectionTarget } from "@/lib/connection-target";
import { firebaseSession } from "@/lib/firebase";

/** An attachment as the client holds it: with the public address it was stored with, or with only its id. */
export type AttachmentAddressSource = {
	id?: string;
	url?: string | null;
	objectKey?: string;
};

const STORAGE_ORIGIN = "https://storage.googleapis.com";

/**
 * Whether `url` is the one shape a stored attachment's own address has: the
 * path-style address, over HTTPS on Cloud Storage, of the attachment's own
 * object key in some bucket. Anything else, an address a sender made up
 * included, is not opened as it is; a stored attachment then goes through the
 * server, which also checks the bucket.
 */
export const isOwnObjectAddress = (
	url: string,
	objectKey: string | undefined
): boolean => {
	if (!objectKey) return false;
	try {
		const parsed = new URL(url);
		if (parsed.origin !== STORAGE_ORIGIN || parsed.search || parsed.hash) {
			return false;
		}
		const [bucket = "", ...rest] = parsed.pathname.slice(1).split("/");
		return bucket !== "" && decodeURIComponent(rest.join("/")) === objectKey;
	} catch {
		return false;
	}
};

/** An id the server gave a stored attachment. An optimistic message's attachments carry ids of their own, which open nothing yet. */
const STORED_ATTACHMENT_ID = /^[0-9a-fA-F]{24}$/;

/**
 * How long before a signed address expires it is treated as expired already,
 * so a link is not opened, or a media element left to fetch, with an address
 * that dies on the way.
 */
export const RENEW_BEFORE_EXPIRY_MS = 20 * 1000;
const MS_PER_SECOND = 1000;

/**
 * The server's attachment route for stored attachment `id`
 * (docs/adr/43-attachments-are-private-and-read-through-short-lived-signed-addresses.md).
 * It carries no credential: the ID token goes in a header, never in an address.
 */
export const attachmentRouteAddress = (serverUrl: string, id: string): string =>
	`${serverUrl}/api/attachment?${new URLSearchParams({ id }).toString()}`;

/** A signed address the server handed out, and when it stops working; null for a stored public address, which does not. */
export type OpenedAttachment = { url: string; expiresAt: number | null };

/**
 * Asks the server for an address that opens stored attachment `id`, signing
 * in with the ID token in the bearer header. The answer is an address signed
 * for that one file that expires within minutes, so it is what an element may
 * carry; the sign-in never is.
 */
export async function openAttachment(id: string): Promise<OpenedAttachment> {
	const idToken = await firebaseSession.getIdToken();
	if (!idToken) throw new Error("Not signed in");
	const res = await fetch(
		attachmentRouteAddress(getConnectionTarget().serverUrl, id),
		{
			headers: {
				authorization: `Bearer ${idToken}`,
				accept: "application/json",
			},
			cache: "no-store",
		}
	);
	if (!res.ok) throw new Error(`Attachment route answered ${res.status}`);
	const body: unknown = await res.json();
	const { url, expiresInSeconds } = (body ?? {}) as {
		url?: unknown;
		expiresInSeconds?: unknown;
	};
	if (typeof url !== "string") {
		throw new Error("Attachment route answered no address");
	}
	return {
		url,
		expiresAt:
			typeof expiresInSeconds === "number"
				? Date.now() + expiresInSeconds * MS_PER_SECOND
				: null,
	};
}

const hasExpired = (opened: OpenedAttachment): boolean =>
	opened.expiresAt !== null &&
	Date.now() >= opened.expiresAt - RENEW_BEFORE_EXPIRY_MS;

export type AttachmentAddress = {
	/** Where to open the file; undefined while there is nowhere to open it yet. */
	address: string | undefined;
	/**
	 * For a link's `onClick`. A held address that has expired is not followed:
	 * a fresh one is asked for and opened in a new tab instead.
	 */
	openFresh: (event: MouseEvent<HTMLAnchorElement>) => void;
	/**
	 * For an element's `onError`. When the held address has expired, a fresh
	 * one is asked for, which the element then loads, and this is true; a
	 * failure of an address still in date is not retried.
	 */
	renewIfExpired: () => boolean;
};

/**
 * Where to open `file`: the address it was stored with, unchanged, when that
 * is its own object's storage address, or otherwise an address the server
 * signs for it once it has checked the signed-in user may read it. That
 * address expires, so it is asked for again when a link to it is opened late
 * or an element fails on it.
 */
export function useAttachmentAddress(
	file: AttachmentAddressSource
): AttachmentAddress {
	const ownAddress =
		file.url && isOwnObjectAddress(file.url, file.objectKey)
			? file.url
			: undefined;
	const storedId =
		!ownAddress && file.id && STORED_ATTACHMENT_ID.test(file.id)
			? file.id
			: undefined;
	const [opened, setOpened] = useState<OpenedAttachment | null>(null);
	const openedRef = useRef<OpenedAttachment | null>(null);
	const pending = useRef<Promise<string | undefined> | null>(null);
	const currentId = useRef(storedId);
	currentId.current = storedId;

	const renew = useCallback((): Promise<string | undefined> => {
		if (!storedId) return Promise.resolve(undefined);
		pending.current ??= openAttachment(storedId)
			.then(
				(next) => {
					if (currentId.current !== storedId) return;
					openedRef.current = next;
					setOpened(next);
					return next.url;
				},
				// Nothing to open: the element keeps what it has, and the next
				// opening or failure asks again.
				() => undefined
			)
			.finally(() => {
				pending.current = null;
			});
		return pending.current;
	}, [storedId]);

	useEffect(() => {
		openedRef.current = null;
		setOpened(null);
		if (storedId) renew();
	}, [storedId, renew]);

	const openFresh = useCallback(
		(event: MouseEvent<HTMLAnchorElement>) => {
			const held = openedRef.current;
			if (!storedId || (held && !hasExpired(held))) return;
			event.preventDefault();
			// Opened now, while the click still allows a new tab, and pointed at
			// the fresh address once it arrives.
			const tab = window.open("", "_blank");
			if (tab) tab.opener = null;
			renew().then((url) => {
				if (!tab) return;
				if (url) tab.location.replace(url);
				else tab.close();
			});
		},
		[storedId, renew]
	);

	const renewIfExpired = useCallback((): boolean => {
		const held = openedRef.current;
		if (!(storedId && held && hasExpired(held))) return false;
		renew();
		return true;
	}, [storedId, renew]);

	return {
		address: ownAddress ?? (storedId ? opened?.url : undefined),
		openFresh,
		renewIfExpired,
	};
}
